import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Stack,
  Image,
  SimpleGrid,
  Container,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from "@chakra-ui/react";

/* =========================
   WAVEFORM BACKGROUND
========================= */
const Waveform = () => {
  const canvasRef = useRef();

  useEffect(() => {
    let audioContext;
    let analyser;
    let dataArray;
    let bufferLength;
    let animationId;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = 300;
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      animationId = requestAnimationFrame(draw);

      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      if (!analyser) return;

      analyser.getByteTimeDomainData(dataArray);

      ctx.beginPath();

      const sliceWidth = w / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * h) / 2;

        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);

        x += sliceWidth;
      }

      ctx.strokeStyle = "rgba(102, 201, 214, 0.75)";
      ctx.lineWidth = 2;
      ctx.stroke();
    };

    const initMic = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        audioContext = new (window.AudioContext || window.webkitAudioContext)();

        const source = audioContext.createMediaStreamSource(stream);

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;

        bufferLength = analyser.fftSize;
        dataArray = new Uint8Array(bufferLength);

        source.connect(analyser);

        draw();
      } catch (err) {
        console.log("Mic permission denied or not available", err);
      }
    };

    initMic();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      if (audioContext) audioContext.close();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "300px",
        opacity: 0.45,
        pointerEvents: "none",
      }}
    />
  );
};

/* =========================
   SCROLL REVEAL
========================= */
const useReveal = () => {
  const ref = useRef();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 },
    );

    if (ref.current) obs.observe(ref.current);

    return () => obs.disconnect();
  }, []);

  return [ref, visible];
};

/* =========================
   SECTION WRAPPER (CONTRAST FIX)
========================= */
const Section = ({ id, bg, children }) => {
  const [ref, visible] = useReveal();

  return (
    <Box
      id={id}
      ref={ref}
      bg={bg}
      py="24"
      px="6"
      transition="0.8s ease"
      opacity={visible ? 1 : 0}
      transform={visible ? "translateY(0px)" : "translateY(40px)"}
      position="relative"
    >
      {/* soft divider */}
      <Box
        position="absolute"
        top="0"
        left="8%"
        right="8%"
        height="1px"
        bg="rgba(51,184,202,0.25)"
      />

      <Container maxW="6xl">{children}</Container>
    </Box>
  );
};

/* =========================
   MAIN PAGE
========================= */
export default function BeatVoicesPage() {
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    document.title = "Beat Voices - Live Looping App";
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box bg="#0D0939" color="white" fontFamily="'Inter', sans-serif">
      <Modal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalBody display="flex" justifyContent="center">
            <Image src={selectedImage} borderRadius="lg" />
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* HEADER */}
      <Flex
        position="sticky"
        top="0"
        zIndex="10"
        bg="rgba(13, 9, 57, 0.85)"
        backdropFilter="blur(14px)"
        px="8"
        py="4"
        justify="space-between"
        align="center"
        borderBottom="1px solid rgba(51,184,202,0.3)"
      >
        <Flex align="center" gap="3">
          <Image
            src={require("../images/beat-voices/beat-voices-logo.png")}
            boxSize="34px"
          />
          <Heading size="sm">Beat Voices</Heading>
        </Flex>

        <Stack direction="row" spacing="6" fontSize="sm">
          <Text cursor="pointer" onClick={() => scrollTo("home")}>
            Home
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("features")}>
            Features
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("screenshots")}>
            Screens
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("pricing")}>
            Pricing
          </Text>
          <Text cursor="pointer" onClick={() => scrollTo("faq")}>
            FAQ
          </Text>
        </Stack>

        <Button
          bg="#33B8CA"
          color="black"
          as="a"
          href="https://play.google.com/store/apps/details?id=com.staminexa.beatvoices"
          target="_blank"
        >
          Get App
        </Button>
      </Flex>

      {/* HERO (WITH WAVEFORM) */}
      <Section id="home" bg="linear-gradient(to bottom, #0D0939, #120b4d)">
        <Box position="relative" overflow="hidden">
          <Waveform />

          <Flex
            position="relative"
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
          >
            <Stack spacing="6" maxW="lg">
              <Heading fontSize={{ base: "3xl", md: "5xl" }}>
                Live Looping for Real Performers
              </Heading>

              <Text opacity="0.8">
                Unleash your creativity with Beat Voices and build a more
                expressive live looping experience.
              </Text>
              <Text fontSize="xs" opacity="0.5">
                Allow microphone access to activate live waveform
              </Text>

              <Stack direction="row">
                <Button
                  bg="#33B8CA"
                  color="black"
                  as="a"
                  href="https://play.google.com/store/apps/details?id=com.staminexa.beatvoices"
                  target="_blank"
                >
                  Start Now
                </Button>

                <Button
                  variant="outline"
                  borderColor="#33B8CA"
                  color="white"
                  _hover={{ bg: "rgba(51,184,202,0.15)" }}
                  as="a"
                  href="https://youtube.com/shorts/G0wqNd5xnKk"
                  target="_blank"
                >
                  Watch Demo
                </Button>
              </Stack>
            </Stack>

            <Image
              src={require("../images/beat-voices/hero.png")}
              maxW="460px"
              mt={{ base: 10, md: 0 }}
            />
          </Flex>
        </Box>
      </Section>

      {/* FEATURES (DARKER) */}
      <Section id="features" bg="#0f0a42">
        <Heading textAlign="center" mb="10" color="#33B8CA">
          Features
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="8">
          {[
            ["Real-Time Looping", "Ultra-low latency performance engine."],
            ["Pro Stage Mode", "Optimized for live DJs & performers."],
            ["Smart Sync", "Automatic BPM and rhythm detection."],
          ].map((f, i) => (
            <Box
              key={i}
              bg="rgba(255,255,255,0.03)"
              p="6"
              borderRadius="lg"
              border="1px solid rgba(51,184,202,0.2)"
            >
              <Heading size="md" mb="2">
                {f[0]}
              </Heading>
              <Text opacity="0.8">{f[1]}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      {/* SCREENSHOTS (LIGHTER) */}
      <Section id="screenshots" bg="#120b4d">
        <Heading textAlign="center" mb="10" color="#33B8CA">
          Screenshots
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 4 }} spacing="6">
          {["ss-1.jpg", "ss-2.jpg", "ss-3.jpg", "ss-4.jpg"].map((img, i) => (
            <Image
              key={i}
              src={require(`../images/beat-voices/${img}`)}
              borderRadius="lg"
              cursor="pointer"
              transition="0.2s"
              _hover={{ transform: "scale(1.03)" }}
              onClick={() =>
                setSelectedImage(require(`../images/beat-voices/${img}`))
              }
            />
          ))}
        </SimpleGrid>
      </Section>

      {/* PRICING */}
      <Section id="pricing" bg="#0f0a42">
        <Heading textAlign="center" mb="6" color="#33B8CA">
          Pricing
        </Heading>

        <Text textAlign="center" mb="10" opacity="0.7">
          Free plan available. Paid plans include 7-day free trial. Cancel
          anytime.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="8">
          {/* FREE */}
          <Box
            bg="rgba(255,255,255,0.03)"
            p="6"
            borderRadius="lg"
            border="1px solid rgba(255,255,255,0.08)"
          >
            <Heading size="md">Free</Heading>

            <Text fontSize="2xl" mt="2" color="#33B8CA">
              $0
            </Text>

            <Text opacity="0.8">Limited loops, basic features only</Text>

            <Text fontSize="xs" mt="3" opacity="0.6">
              No trial • No subscription required
            </Text>
          </Box>

          {/* PRO MONTHLY */}
          <Box
            bg="rgba(51,184,202,0.10)"
            p="6"
            borderRadius="lg"
            border="1px solid rgba(51,184,202,0.4)"
          >
            <Text fontSize="xs" mb="2" color="#33B8CA">
              MOST POPULAR
            </Text>

            <Heading size="md">Pro Monthly</Heading>

            <Text fontSize="2xl" mt="2" color="white">
              $1.3 / month
            </Text>

            <Text opacity="0.9">Full access to all pro features</Text>

            <Text fontSize="xs" mt="3" opacity="0.8">
              Includes 7-day free trial • Cancel anytime
            </Text>
          </Box>

          {/* YEARLY */}
          <Box
            bg="rgba(255,255,255,0.03)"
            p="6"
            borderRadius="lg"
            border="1px solid rgba(255,255,255,0.08)"
          >
            <Heading size="md">Pro Yearly</Heading>

            <Text fontSize="2xl" mt="2" color="#33B8CA">
              $12 / year
            </Text>

            <Text opacity="0.8">Best value for long-term users</Text>

            <Text fontSize="xs" mt="3" opacity="0.7">
              Includes 7-day free trial
            </Text>
          </Box>
        </SimpleGrid>
      </Section>

      {/* FAQ */}
      <Section id="faq" bg="#120b4d">
        <Heading textAlign="center" mb="10" color="#33B8CA">
          FAQ
        </Heading>

        <Accordion allowToggle>
          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                What is Beat Voices used for?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              It is used for real-time audio looping and live music performance.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Is Beat Voices available on Android?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              Yes, it is fully available on Android via Google Play Store.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Does Beat Voices work offline?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              No, it requires internet connection for authentication and
              syncing.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Is Beat Voices free or paid?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              It offers a free trial, but full features require subscription.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Can I use it for live performances?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              Yes, it is designed for live stage and DJ performances.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                What is a looping app in music?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              A looping app records audio and repeats it in real-time layers.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Does Beat Voices support MIDI or external devices?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              Future updates will include MIDI and external controller support.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Is internet required to use Beat Voices?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              Yes, internet is required for account verification and syncing.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Can I export my recordings?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              Yes, you can export loops in high-quality audio formats.
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem border="none">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                Who is Beat Voices for?
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel opacity="0.8">
              Musicians, DJs, performers, and sound designers.
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Section>

      {/* FOOTER */}
      <Box py="10" textAlign="center" opacity="0.6">
        © {new Date().getFullYear()} Beat Voices
      </Box>
    </Box>
  );
}
